import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

const NAMES = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  ovh: 'OVH'
};

const RANGES = [
  { key: '1m', label: '1 mois' },
  { key: '3m', label: '3 mois' },
  { key: '6m', label: '6 mois' },
  { key: '12m', label: '12 mois' },
  { key: '24m', label: '24 mois' },
];

export const getStartDate = (key) => {
  const now = new Date();
  switch (key) {
    case '1m': return new Date(now.getFullYear(), now.getMonth(), 1);
    case '3m': return new Date(now.getFullYear(), now.getMonth() - 2, 1);
    case '6m': return new Date(now.getFullYear(), now.getMonth() - 5, 1);
    case '12m': return new Date(now.getFullYear(), now.getMonth() - 11, 1);
    case '24m': return new Date(now.getFullYear(), now.getMonth() - 23, 1);
    default: return new Date(now.getFullYear(), now.getMonth() - 5, 1);
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const formatDate = (d) => {
    // Monthly key format: "2025-01"
    const [y, m] = d.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const realEntries = payload.filter(entry => entry.value > 0 && !entry.dataKey.endsWith('_forecast'));
  const forecastEntries = payload.filter(entry => entry.value > 0 && entry.dataKey.endsWith('_forecast'));
  const realProviders = new Set(realEntries.map(e => e.dataKey));
  const visibleForecasts = forecastEntries.filter(e => !realProviders.has(e.dataKey.replace('_forecast', '')));

  const allEntries = [...realEntries, ...visibleForecasts].sort((a, b) => b.value - a.value);

  return (
    <div className="bg-popover/95 backdrop-blur-sm border border-border shadow-xl rounded-lg p-3 text-sm">
      <p className="font-medium mb-2 capitalize">{formatDate(label)}</p>
      {allEntries.map((entry, index) => {
        const isForecast = entry.dataKey.endsWith('_forecast');
        const providerKey = isForecast ? entry.dataKey.replace('_forecast', '') : entry.dataKey;
        return (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
              <span>{NAMES[providerKey] || providerKey.toUpperCase()}{isForecast ? ' (Prév.)' : ''}</span>
            </div>
            <span className="font-medium tabular-nums">{entry.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
          </div>
        );
      })}
    </div>
  );
};

export const CostChart = ({ data, title, range, onRangeChange }) => {
  const chartTitle = title || 'Évolution des coûts';

  if (!data || !data.providers) {
    return (
      <Card className="flex flex-col" data-testid="cost-chart">
        <CardHeader>
          <CardTitle className="text-lg font-[Outfit]">{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[350px]">
          <p className="text-muted-foreground">Chargement des données...</p>
        </CardContent>
      </Card>
    );
  }

  const activeProviders = data.providers.filter(
    p => p.cost_by_day && p.cost_by_day.length > 0
  );

  if (activeProviders.length === 0) {
    return (
      <Card className="flex flex-col" data-testid="cost-chart">
        <CardHeader>
          <CardTitle className="text-lg font-[Outfit]">{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[350px]">
          <p className="text-muted-foreground">Aucune donnée de coût disponible. Connectez et synchronisez un provider.</p>
        </CardContent>
      </Card>
    );
  }

  const startDate = getStartDate(range);
  const startStr = startDate.toISOString().slice(0, 10);

  // Aggregate by month
  const monthMap = new Map();

  activeProviders.forEach(provider => {
    provider.cost_by_day.forEach(day => {
      if (day.date < startStr) return;
      const month = day.date.slice(0, 7); // "YYYY-MM"
      if (!monthMap.has(month)) {
        monthMap.set(month, { date: month });
      }
      const entry = monthMap.get(month);
      entry[provider.provider] = (entry[provider.provider] || 0) + day.cost;
    });
  });

  const chartData = [];
  monthMap.forEach(value => chartData.push(value));
  chartData.sort((a, b) => a.date.localeCompare(b.date));

  const providerKeys = activeProviders.map(p => p.provider);

  // --- Forecast projection for current month ---
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const forecastKeys = [];

  // Compute forecast for current month
  const providerMonthTotals = {};
  const providerMonthDays = {};

  activeProviders.forEach(provider => {
    let total = 0;
    let days = 0;
    provider.cost_by_day.forEach(day => {
      if (day.date.startsWith(currentMonth)) {
        total += day.cost;
        days++;
      }
    });
    if (days > 0) {
      providerMonthTotals[provider.provider] = total;
      providerMonthDays[provider.provider] = days;
    }
  });

  // Check if current month has data and is not complete yet
  const currentMonthEntry = chartData.find(d => d.date === currentMonth);
  const today = now.getDate();
  if (currentMonthEntry && today < daysInMonth) {
    // Add forecast as a separate month entry (next month placeholder to draw the line)
    // Set forecast value on the current month point to connect lines
    providerKeys.forEach(provider => {
      if (providerMonthDays[provider]) {
        const forecastKey = provider + '_forecast';
        if (!forecastKeys.includes(forecastKey)) forecastKeys.push(forecastKey);
        // On current month: real value + forecast start point
        currentMonthEntry[forecastKey] = currentMonthEntry[provider] || 0;
      }
    });

    // Add end-of-month forecast point
    const forecastEntry = { date: currentMonth + '-forecast' };
    providerKeys.forEach(provider => {
      if (providerMonthDays[provider]) {
        const forecastVal = (providerMonthTotals[provider] / providerMonthDays[provider]) * daysInMonth;
        const forecastKey = provider + '_forecast';
        forecastEntry[forecastKey] = parseFloat(forecastVal.toFixed(2));
        if (!forecastKeys.includes(forecastKey)) forecastKeys.push(forecastKey);
      }
    });
    chartData.push(forecastEntry);
  }

  const formatDate = (value) => {
    if (value.endsWith('-forecast')) {
      const [y, m] = value.split('-');
      const date = new Date(parseInt(y), parseInt(m) - 1, 1);
      return 'Fin ' + date.toLocaleDateString('fr-FR', { month: 'short' });
    }
    const [y, m] = value.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, 1);
    return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };

  const tickInterval = chartData.length > 12 ? Math.floor(chartData.length / 8) : 0;

  return (
    <Card className="flex flex-col" data-testid="cost-chart">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-[Outfit]">{chartTitle}</CardTitle>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => onRangeChange(r.key)}
              className={
                'px-3 py-1 text-xs font-medium rounded-md transition-colors ' +
                (range === r.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[350px]">
            <p className="text-muted-foreground">Aucune donnée sur cette période.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                {providerKeys.map(provider => (
                  <linearGradient key={provider} id={'gradient-' + provider} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[provider]} stopOpacity={0.25}/>
                    <stop offset="95%" stopColor={COLORS[provider]} stopOpacity={0.02}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={formatDate}
                interval={tickInterval}
                className="text-muted-foreground"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => value.toLocaleString('fr-FR') + ' €'}
                className="text-muted-foreground"
                axisLine={false}
                tickLine={false}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value) => NAMES[value] || value.toUpperCase()}
                wrapperStyle={{ fontSize: '13px', paddingTop: '12px' }}
                payload={providerKeys.map(provider => ({
                  value: provider,
                  type: 'line',
                  color: COLORS[provider],
                }))}
              />
              {providerKeys.map(provider => (
                <Area
                  key={provider}
                  type="monotone"
                  dataKey={provider}
                  stroke={COLORS[provider]}
                  fill={'url(#gradient-' + provider + ')'}
                  strokeWidth={2}
                  name={provider}
                  connectNulls={true}
                  dot={chartData.length <= 15}
                />
              ))}
              {forecastKeys.map(key => {
                const provider = key.replace('_forecast', '');
                return (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[provider]}
                    fill="none"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    strokeOpacity={0.6}
                    name={key}
                    connectNulls={true}
                    dot={false}
                    legendType="none"
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
